/**
 * #712 — the Value Dashboard's opt-in weekly slate for NFL and CFB.
 *
 * One module owns the whole notion: which window is on screen, how it reads in
 * the toolbar, and which board selections may have one at all. The three share
 * a single design decision and are always used together — splitting them across
 * the dashboard, the toolbar and the store is how the control offered and the
 * rows filtered start to disagree.
 *
 * The window is expressed as an INCLUSIVE pair of bare `YYYY-MM-DD` calendar
 * dates, and Day is the degenerate pair `[d, d]`. That is deliberate: the store
 * keeps one code path, so Day mode cannot drift from what it does today. Bare
 * ISO dates also compare lexicographically, so every membership test downstream
 * is a string comparison — no clock, no offset, and DST-proof by construction.
 *
 * Alignment is Monday–Sunday, borrowed from `weekRangeContaining` so the app has
 * exactly one week convention (Manager → Trader Results already uses it). It is
 * a plain CALENDAR week and claims nothing about league-week identity: an NFL
 * week runs Thu→Mon, so its Monday-night game deliberately falls in the next
 * window rather than having a boundary bent to fit. #712 forbids inferring a
 * league week number from calendar arithmetic, and this is how that promise is
 * kept structurally instead of only in the label.
 */

import { weekRangeContaining } from '@/lib/infra/weekRange';
import { underlyingSport, type DashboardSport } from '@/lib/routing/dashboardSport';
import type { MarketType, SlateRange } from './types';

/** An inclusive span of calendar dates, both bare `YYYY-MM-DD`. Day mode's
 *  window has `startIso === endIso`. */
export interface SlateWindow {
  startIso: string;
  endIso: string;
}

const ISO_DATE = /^(\d{4})-(\d{2})-(\d{2})$/;

const WEEK_LENGTH_DAYS = 7;

/** Sports whose boards may carry a weekly slate. Football only, by product
 *  contract — every other sport keeps its daily view. */
const WEEKLY_SLATE_SPORTS: ReadonlySet<string> = new Set(['nfl', 'cfb']);

/** Boards a weekly slate applies to: the game-shaped ones. A championship
 *  future is one market listing many teams, not a game with a date on it. */
const WEEKLY_SLATE_MARKET_TYPES: ReadonlySet<MarketType> = new Set<MarketType>([
  'moneyline',
  'spread-total',
]);

/** Shift a bare ISO date by whole days, staying in pure calendar arithmetic.
 *  Built through `Date.UTC` so the result can't be nudged by a local DST
 *  transition — a date is a date, not an instant. Returns the input unchanged
 *  when it isn't a date at all. */
export function addDaysIso(iso: string, days: number): string {
  const match = ISO_DATE.exec(iso);
  if (!match) return iso;
  const shifted = new Date(Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3]) + days));
  const yyyy = String(shifted.getUTCFullYear()).padStart(4, '0');
  const mm = String(shifted.getUTCMonth() + 1).padStart(2, '0');
  const dd = String(shifted.getUTCDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

/**
 * The window on screen for an anchor date and a slate range.
 *
 * A malformed anchor degrades to day semantics rather than throwing.
 * `mdyToIso` in the dashboard is a loose split-and-rejoin over whatever the
 * date input holds, and `weekRangeContaining` throws on anything that isn't
 * `YYYY-MM-DD` — an unparseable date must leave the board showing nothing, the
 * way it does today, not white-screen it.
 */
export function slateWindow(anchorIso: string, range: SlateRange): SlateWindow {
  if (range !== 'week' || !ISO_DATE.test(anchorIso)) {
    return { startIso: anchorIso, endIso: anchorIso };
  }
  const startIso = weekRangeContaining(anchorIso).weekStartDate;
  return { startIso, endIso: addDaysIso(startIso, WEEK_LENGTH_DAYS - 1) };
}

/**
 * Step the ANCHOR by one whole window — a day in Day mode, seven in Week.
 *
 * Moving the anchor rather than the window start is what makes mode switching
 * lossless: the anchor keeps its weekday offset inside the window, so Week →
 * Day → Week returns to the same place instead of snapping to a boundary.
 */
export function shiftSlate(anchorIso: string, range: SlateRange, delta: number): string {
  return addDaysIso(anchorIso, delta * (range === 'week' ? WEEK_LENGTH_DAYS : 1));
}

/**
 * Every calendar date in the window, in order — one entry in Day mode.
 *
 * For the per-date reads that can't take a range: the analytics archive is
 * queried one day at a time, so a weekly slate has to enumerate itself to stay
 * truthful about the range it claims.
 */
export function slateWindowDates(window: SlateWindow): string[] {
  const dates: string[] = [window.startIso];
  let day = window.startIso;
  // Bounded by the widest window that exists. A malformed window — whose dates
  // `addDaysIso` can't advance — stops on the first non-advancing step and
  // yields the single date it started on, matching what day mode would do.
  while (dates.length < WEEK_LENGTH_DAYS) {
    const next = addDaysIso(day, 1);
    if (next === day || next > window.endIso) break;
    dates.push(next);
    day = next;
  }
  return dates;
}

/**
 * A bare ISO date as the dashboard's date pill writes it (`09/03/2026`).
 *
 * Deliberately a loose split-and-rejoin, like its `mdyToIso` counterpart: a
 * value it can't read passes through unchanged rather than throwing, so a
 * malformed date shows as itself instead of white-screening a board.
 */
export function isoToMdy(iso: string): string {
  const [yyyy, mm, dd] = iso.split('-');
  return yyyy && mm && dd ? `${mm}/${dd}/${yyyy}` : iso;
}

/**
 * Midnight LOCAL time on a bare ISO date, in epoch ms.
 *
 * Local, not UTC, because it is compared against event start instants to answer
 * "is this game on or after the day the trader is looking at" — and the day the
 * trader is looking at is their own. `NaN` for anything that isn't a date;
 * callers test with `Number.isFinite` rather than trusting the comparison.
 */
export function startOfIsoDayMs(iso: string): number {
  const [yyyy, mm, dd] = iso.split('-').map(Number);
  if (!yyyy || !mm || !dd) return Number.NaN;
  return new Date(yyyy, mm - 1, dd).getTime();
}

/** True when the window contains the given date. Both ends inclusive. */
export function slateWindowContains(window: SlateWindow, iso: string): boolean {
  return iso >= window.startIso && iso <= window.endIso;
}

const MONTH_LABEL = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
] as const;

function monthDayLabel(iso: string): string | null {
  const match = ISO_DATE.exec(iso);
  if (!match) return null;
  const month = MONTH_LABEL[Number(match[2]) - 1];
  return month ? `${month} ${Number(match[3])}` : null;
}

/**
 * The toolbar's range text — `Sep 7 – Sep 13`, both ends named in full so the
 * span is exact and inclusive at a glance rather than implied by a start plus
 * arithmetic the reader has to do. A window spanning two years names both
 * (`Dec 28, 2026 – Jan 3, 2027`), since "Dec 28 – Jan 3" alone is genuinely
 * ambiguous about which December.
 *
 * Returns null for a window it can't name, so the caller can fall back to the
 * plain date pill rather than render a broken label.
 */
export function formatSlateRange(window: SlateWindow): string | null {
  const start = monthDayLabel(window.startIso);
  const end = monthDayLabel(window.endIso);
  if (!start || !end) return null;
  const startYear = window.startIso.slice(0, 4);
  const endYear = window.endIso.slice(0, 4);
  return startYear === endYear
    ? `${start} – ${end}`
    : `${start}, ${startYear} – ${end}, ${endYear}`;
}

/**
 * Whether the board on screen may carry a weekly slate at all.
 *
 * The parent owns this and hands the same answer to the toolbar's control and
 * to the filter, for the reason `cfbBoardSelected` exists: a toolbar that
 * offers a control the board doesn't apply — or applies one it doesn't offer —
 * is precisely the failure the fence prevents.
 *
 * Every selected sport must be football, so the NFL + CFB weekend board
 * qualifies while NFL + MLB does not. "All sports" is excluded outright: a
 * weekly window there would silently broaden every unrelated sport's board,
 * which the #712 guardrails forbid.
 */
export function weeklySlateEligible(params: {
  selectedSports: ReadonlySet<DashboardSport>;
  marketType: MarketType;
  allSports: boolean;
}): boolean {
  const { selectedSports, marketType, allSports } = params;
  if (allSports || !WEEKLY_SLATE_MARKET_TYPES.has(marketType)) return false;
  // An empty selection IS "all sports" on the moneyline board — never football
  // alone, so it can't qualify by vacuous truth.
  if (selectedSports.size === 0) return false;
  for (const sport of selectedSports) {
    if (!WEEKLY_SLATE_SPORTS.has(underlyingSport(sport))) return false;
  }
  return true;
}
